import { Content, Header, Page, SelectItem, Table, TableColumn } from '@backstage/core-components';
import { Box, Button, Card, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, TextField, Typography } from '@material-ui/core';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import CachedIcon from '@material-ui/icons/Cached';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import RefreshIcon from '@material-ui/icons/Refresh';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useRequestService } from '../../services/RequestService';
import { REQUEST_TYPE_LABEL_MAP } from './mappings';
import { capitalize, startCase } from 'lodash';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { ButtonGroup } from '@mui/material';
import { AlertMessage } from '../../components/alert';
import { SelectInput } from '../../components/input';
import { RequestType, RequestStatus, ListRequestItem, requestManagerCancelPermission, requestManagerFinishPermission, requestManagerCreatePermission } from '@internal/backstage-plugin-request-manager-common';
import SimpleConfirmDialog from '../../components/dialog';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { Autocomplete } from '@material-ui/lab';
import useAsync from 'react-use/esm/useAsync';

interface TableRequest {
  id: string;
  type: RequestType;
  assetType: string;
  name: string;
  systemRef: JSX.Element;
  createdAt: Date;
  status: JSX.Element;
  action: JSX.Element;
}

interface ModalIntention {
    title: string
    text?: string | JSX.Element
    content?: JSX.Element
    confirm: Function
    args?: any
    system?: string
}

const NewRequestButton = () => {
    const navigate = useNavigate();

    return (
        <Box minWidth="131px">
            <Button onClick={() => navigate('new-request')} variant="contained" color="secondary">
                New request
            </Button>
        </Box>
    );
};


const RequestTable = ({ requests, loading }: { requests: TableRequest[], loading: boolean }) => {
    const columns: TableColumn[] = [
        { title: 'System', field: 'systemRef', type: 'string', highlight: true },
        { title: 'Type', field: 'type', type: 'string', highlight: true },
        { title: 'Name', field: 'name', type: 'string' },
        { title: 'Asset', field: 'assetType', type: 'string' },
        { title: 'Created at', field: 'createdAt', type: 'datetime' },
        { title: 'Status', field: 'status', type: 'string' },
        { title: 'Action', field: 'action', type: 'string' }
    ];

    return (
        <Table
            options={{ pageSize: 20, emptyRowsWhenPaging: false }}
            title={`All requests (${requests.length})`}
            columns={columns}
            data={requests}
            totalCount={requests.length}
            isLoading={loading}
            emptyContent={
                <Box p={2}>
                    <Typography align='center'>No requests found. Update the filters or create a new request.</Typography>
                </Box>
            }
        />
    );
};

const TableFilters = ({
    statusOptions,
    typeOptions,
    systemOptions,
    setStatusFilter,
    setTypeFilter,
    setSystemFilter,
    selectedStatus,
    selectedType,
    selectedSystem,
}: {
    statusOptions: { value: any, label: string }[];
    typeOptions: { value: any, label: string }[];
    systemOptions: { value: any, label: string }[];
    setStatusFilter: React.Dispatch<React.SetStateAction<RequestStatus | undefined>>;
    setTypeFilter: React.Dispatch<React.SetStateAction<RequestType | undefined>>;
    setSystemFilter: React.Dispatch<React.SetStateAction<string | undefined>>;
    selectedStatus?: RequestStatus;
    selectedType?: RequestType;
    selectedSystem?: string;
}) => {
    return (
        <>
            <Box minWidth="200px">
                <SelectInput
                    margin="dense"
                    label="Status"
                    value={selectedStatus}
                    options={statusOptions}
                    onChange={value => setStatusFilter(value as RequestStatus)}
                    required={false}
                />
            </Box>

            <Box minWidth="200px">
                <SelectInput
                    margin="dense"
                    label="Type"
                    value={selectedType}
                    options={typeOptions}
                    onChange={value => setTypeFilter(value as RequestType)}
                    required={false}
                />
            </Box>

            <Box minWidth="280px">
                <Autocomplete
                    options={systemOptions}
                    getOptionLabel={(option) => option.label}
                    value={systemOptions.find((option) => option.value === selectedSystem) || null}
                    onChange={(_, value) => setSystemFilter(value?.value)}
                    renderInput={(params) => (
                        <TextField {...params} label="System" variant="outlined" margin='dense' fullWidth />
                    )}
                />
            </Box>
        </>
    );
};

const statusIconMap: Record<RequestStatus, any> = {
    pending: <AccessTimeIcon fontSize="small" style={{ color: '#FFA726' }} />,
    processing: <CachedIcon fontSize="small" style={{ color: '#1976D2' }} />,
    done: <CheckCircleOutlineIcon fontSize="small" style={{ color: '#1DB954' }} />,
    cancelled: <ErrorOutlineIcon fontSize="small" style={{ color: '#E53935' }} />,
};

const RequestActions = ({
    request,
    onCancel,
    onFinish,
}: {
    request: ListRequestItem,
    onCancel: () => void,
    onFinish: () => void,
}) => {
    if (request.status === 'cancelled' || request.status === 'done')
        return <></>;

    const buttonStyle = { minWidth: '87px', maxHeight: '40px' };
    return (
        <ButtonGroup>
            <RequirePermission permission={requestManagerCancelPermission} errorPage={<></>}>
                <Button variant="outlined" color='secondary' onClick={() => onCancel()} style={buttonStyle}>
                    Cancel
                </Button>
            </RequirePermission>
            {(request.status === 'pending' || request.status === 'processing') && (
                <RequirePermission permission={requestManagerFinishPermission} errorPage={<></>}>
                    <Button variant="outlined" color='secondary' onClick={() => onFinish()} style={buttonStyle}>
                        Finish
                    </Button>
                </RequirePermission>
            )}
        </ButtonGroup>
    );
};

export const RequestPage = () => {
    const { state } = useLocation();
    const requestService = useRequestService();

    const { value: systems } = useAsync(async () => await requestService.listSystems(), []);
    const [selectedStatus, setSelectedStatus] = useState<RequestStatus | undefined>('pending');
    const [selectedType, setSelectedType] = useState<RequestType | undefined>();
    const [selectedSystem, setSelectedSystem] = useState<string  | undefined>(state?.system);
    const [refreshKey, setRefreshKey] = useState(0);
    const [confirmModalOpen, setConfirmModalOpen] = useState<ModalIntention | undefined>();
    const [confirmOperationOpen, setConfirmOperationOpen] = useState<ModalIntention | undefined>();

    const typeOptions = [
        { label: 'All', value: '' },
        { label: 'Domain Structure', value: 'domain-structure' },
        { label: 'Component', value: 'component' },
        { label: 'Resource', value: 'resource' },
        { label: 'Product', value: 'product' },
    ];
    const statusOptions: SelectItem[] = [
        { label: 'All', value: '' },
        { label: 'Pending', value: 'pending' },
        { label: 'Processing', value: 'processing' },
        { label: 'Done', value: 'done' },
        { label: 'Cancelled', value: 'cancelled' },
    ];

    const triggerManualRefresh = () => setRefreshKey(prev => prev + 1);

    const fetchRequests = async (status?: RequestStatus, type?: RequestType, system?: string) => {
        const data = await requestService.listRequests(status, type, system);
        return data.map((request) => {

            let confirmAction = null;

            if (request.type === 'product' || request.assetType.startsWith('totvs-')) {
                confirmAction = (
                    <RequestActions
                        request={request}
                        onCancel={() => setConfirmModalOpen(cancelAction(request))}
                        onFinish={() => setConfirmOperationOpen(finishAction(request))}
                    />
                );
            } else {
                confirmAction = (
                    <RequestActions
                        request={request}
                        onCancel={() => setConfirmModalOpen(cancelAction(request))}
                        onFinish={() => setConfirmModalOpen(finishAction(request))}
                    />
                );
            }

            return {
                id: request.id,
                type: REQUEST_TYPE_LABEL_MAP[request.type],
                assetType: startCase(request.assetType),
                name: request.name,
                systemRef: <EntityRefLink entityRef={request.system} />,
                createdAt: new Date(request.createdAt),
                status: (
                    <Box display="flex" alignItems="center" style={{ gap: '4px' }}>
                        {statusIconMap[request.status]}
                        <span>{capitalize(request.status)}</span>
                    </Box>
                ),
                action: confirmAction
            };
        }) as TableRequest[];
    };

    const { value: requests, loading } = useAsync(async () => {
        return fetchRequests(selectedStatus, selectedType, selectedSystem);
    }, [selectedStatus, selectedType, selectedSystem, refreshKey]);

    function cancelAction(req: ListRequestItem): ModalIntention {
        return {
            text: 'You are about to cancel this request. After cancellation, no further action will be taken. Please confirm if you want to proceed.',
            title: 'Cancel request?',
            confirm: async () => {
                await requestService.cancelRequest(req.id);
                triggerManualRefresh()
            }
        }
    }

    const docRefText = <p>See <a target="_blank" href="https://open.totvs.io/docs/supply-engenharia/system/backstage/funcionalidades/onboarding/doc-tecnica/#etapas-manuais">documentation</a> for more details.</p>;

    function finishAction(req: ListRequestItem): ModalIntention {
        if (req.type === 'product') {

            const args = {
                repo: '',
                gcp_prd: '',
                gcp_stg: ''
            };

            return {
                text: 'Complete product setup with TotvsApps provisioning information.',
                title: 'Finish product request?',
                system: req.system,
                args: args,
                confirm: async (arg: any) => {
                    await requestService.completeProduct(req, args)
                    triggerManualRefresh()
                },
                content:
                    <div>
                        <DialogContentText>
                            Enter the informations below exactly as provided by TotvsApps team for {req.system ? <EntityRefLink entityRef={req.system} target='_blank'/> : 'the product'}.
                        </DialogContentText>
                        <Box component="form"
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            style={{ gap: '16px' }}
                            mt={3}
                        >
                            <TextField
                                label='CD Repository Name'
                                variant="outlined"
                                onChange={event => { args.repo = event.target.value }}
                                helperText="CD repository name returned by TotvsApps (e.g. supply-logistica-ai, supply-fsw-savencia)"
                                required
                                fullWidth
                            />
                            <TextField
                                label='GCP Project Production'
                                defaultValue={confirmOperationOpen?.args.gcp_prd}
                                variant="outlined"
                                onChange={event => { args.gcp_prd = event.target.value }}
                                helperText="GCP project ID for production (e.g. tab1784-service-167-322963)"
                                required
                                fullWidth
                            />
                            <TextField
                                label='GCP Project Staging'
                                defaultValue={confirmOperationOpen?.args.gcp_stg}
                                variant="outlined"
                                onChange={event => { args.gcp_stg = event.target.value }}
                                helperText="GCP project ID for staging (e.g. tfc9924-service-167-374429)"
                                required
                                fullWidth
                            />
                        </Box>
                        {docRefText}
                    </div>
            }
        }

        if (req.type === 'resource' && req.assetType.startsWith('totvs-')) {
            const args = { id: '' }
            return {
                text: 'Enter the Client ID for the resource integration.',
                title: 'Finish TotvsApps support element request?',
                system: req.system,
                args: args,
                confirm: async (arg: any) => {
                    await requestService.finishRequest(req.id);
                    await requestService.completeTotvsResource(req, args.id)
                    triggerManualRefresh()
                },
                content:
                    <div>
                        <DialogContentText>
                            Please fill in the Client ID of the resource { startCase(req.assetType) }.
                        </DialogContentText>
                        <TextField
                            label='Client ID'
                            defaultValue={args.id}
                            variant="outlined"
                            onChange={event => { args.id = event.target.value }}
                            required
                            fullWidth
                        />
                        {docRefText}
                    </div>
            }
        }

        const finishText = req.type === 'domain-structure'
            ? 'Please confirm that the domain structure has been created successfully.'
            : 'Confirm that all PRs have been approved and merged by TotvsApps team and no further action is required.';

        return {
            text: <p>
                {finishText}
                {docRefText}
            </p>,
            title: 'Finish request?',
            confirm: async () => {
                await requestService.finishRequest(req.id);
                triggerManualRefresh()
            }
        }
    }

    return (
        <Page themeId="home">
            <Header title="Requests" subtitle="View and manage all requests for new items such as components, resources, and products." />
            <Content>
                <Card style={{ padding: '16px' }}>
                    <Box display="flex" flexDirection="column">
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Box display="flex" alignItems="center" style={{ gap: '16px' }}>
                                <TableFilters
                                    statusOptions={statusOptions}
                                    typeOptions={typeOptions}
                                    systemOptions={systems || []}
                                    setStatusFilter={setSelectedStatus}
                                    setTypeFilter={setSelectedType}
                                    setSystemFilter={setSelectedSystem}
                                    selectedStatus={selectedStatus}
                                    selectedType={selectedType}
                                    selectedSystem={selectedSystem}
                                />

                                <Box display="flex" alignItems="flex-end">
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        style={{ height: 40, minWidth: 40, padding: 0 }}
                                        onClick={triggerManualRefresh}
                                        title="Refresh requests"
                                    >
                                        <RefreshIcon />
                                    </Button>
                                </Box>
                            </Box>

                            <RequirePermission permission={requestManagerCreatePermission} errorPage={<></>}>
                                <NewRequestButton />
                            </RequirePermission>
                        </Box>

                        <Grid item xs={12}>
                            <RequestTable requests={requests || []} loading={loading} />
                        </Grid>
                    </Box>
                </Card>
            </Content>

            <SimpleConfirmDialog
                open={confirmModalOpen !== undefined}
                title={confirmModalOpen?.title || ''}
                text={confirmModalOpen?.text || ''}
                onCancel={() => setConfirmModalOpen(undefined)}
                onConfirm={() => { setConfirmModalOpen(undefined); confirmModalOpen?.confirm() }} />

            <Dialog open={confirmOperationOpen !== undefined} onClose={() => setConfirmOperationOpen(undefined)}>
                <DialogTitle>Finish product request?</DialogTitle>
                <DialogContent>
                    { confirmOperationOpen?.content }
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOperationOpen(undefined)} color="primary">
                    Cancel
                    </Button>
                    <Button onClick={() => { setConfirmOperationOpen(undefined); confirmOperationOpen?.confirm(confirmOperationOpen?.args) }} color="secondary">
                    Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            {state?.message && <AlertMessage message={state.message} type="success" />}
        </Page>
    );
}
